/**
 * ReporteRubros.js — Cuentas por Cobrar por Rubro (matriz Filial × Rubro)
 *
 *    Filial │  Cuotas  │ Agua    │  Total   │ Estado
 *    02     │42,000.00 │10,425.15│52,425.15 │ Moroso
 *
 * Tres vistas sobre el mismo cuadro:
 *  · Saldo al corte — lo que cada filial debe a una fecha, abierto por rubro.
 *    Es la "Revisión de cuenta por cobrar" del contador y cuadra filial por
 *    filial con el estado de cuenta (el backend usa las mismas reglas). Trae
 *    el estado de cobro (Moroso / Arreglo / Cobro judicial), que se cambia
 *    desde la misma tabla.
 *  · Facturado — lo emitido en el período.
 *  · Cobrado — lo que entró en el período, por fecha de pago.
 *
 * Lo que no tiene rubro (saldos iniciales importados) sale en la columna
 * "Sin rubro" con un aviso que lleva a Saldos Iniciales para asignárselo.
 *
 * Los montos ya vienen en la moneda pedida (?moneda=), valorados como en el
 * estado de cuenta; esta pantalla no convierte nada.
 *
 * ── Sobre la presentación ────────────────────────────────────────────────────
 * Antes de la tabla van el resumen y la composición por rubro, que es la
 * pregunta que el reporte contesta. Dentro de la tabla cada celda positiva
 * lleva un tinte proporcional a su magnitud DENTRO DE SU COLUMNA, así se ven
 * los pesos pesados sin leer número por número. Los colores de rubro siguen a
 * la entidad (se calculan sobre el total sin filtrar), no a la posición.
 */
import { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import apiFetch from "../../../../shared/services/apiClient";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import { useMultimoneda } from "../../../../shared/hooks/useMultimoneda";
import { useLang } from "../../../../shared/i18n/useLang";
import { useEsCliente } from "../../../../shared/hooks/useTenantTipo";
import { useTheme } from "../../../../shared/contexts/ThemeContext";
import { downloadExcel } from "../../shared/excelService";
import CurrencyToggle from "../../shared/CurrencyToggle";
import invoiceStyles from "../../invoices/styles/invoice.module.css";

/* Paleta categórica de rubros (validada con el validador de dataviz: CVD,
   normal y contraste, en claro y oscuro). Misma que la dona de Informes: un
   rubro se ve del mismo color en los dos reportes. */
const RUBRO_COLORS = {
  dark:  ["#3987e5", "#d95926", "#199e70", "#c98500", "#d55181", "#008300", "#9085e9", "#e66767"],
  light: ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#4a3aa7", "#e34948"],
};
const GRIS = { dark: "#6b7280", light: "#94a3b8" };
const SIN_RUBRO = 0;
// Créditos de "Guardar sobrante como saldo a favor" todavía sin usar: no son
// un rubro por asignar, así que van en su columna y sin el aviso de asignar.
const SALDO_FAVOR = -1;

const MODO_COLOR = { saldo: "#f97316", facturado: "#3b82f6", cobrado: "#22c55e" };

/* Estado de cobro. Siempre con ícono + etiqueta: el color solo no alcanza. */
const ESTADOS = {
  COBRO_JUDICIAL: { es: "Cobro judicial", en: "Legal collection", corto: ["Judicial", "Legal"], color: "#ef4444", icon: "fa-gavel" },
  ARREGLO:        { es: "Arreglo de pago", en: "Payment plan",    corto: ["Arreglo", "Plan"],   color: "#8b5cf6", icon: "fa-handshake" },
  MOROSO:         { es: "Moroso",          en: "Overdue",         corto: ["Moroso", "Overdue"], color: "#f59e0b", icon: "fa-clock" },
  AL_DIA:         { es: "Al día",          en: "Up to date",      corto: ["Al día", "OK"],      color: "#64748b", icon: "fa-circle-check" },
};

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function rangoMes(offset = 0) {
  const n = new Date();
  return [
    ymd(new Date(n.getFullYear(), n.getMonth() + offset, 1)),
    ymd(new Date(n.getFullYear(), n.getMonth() + offset + 1, 0)),
  ];
}
function rangoAnio() {
  const y = new Date().getFullYear();
  return [`${y}-01-01`, `${y}-12-31`];
}
function fechaCorta(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}
function tinte(hex, alpha) {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
function estadoEfectivo(manual, total) {
  if (ESTADOS[manual]) return manual;
  return total > 0.004 ? "MOROSO" : "AL_DIA";
}

function useNarrow(bp = 880) {
  const [n, setN] = useState(() => (typeof window !== "undefined" ? window.innerWidth < bp : false));
  useEffect(() => {
    const h = () => setN(window.innerWidth < bp);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, [bp]);
  return n;
}

export default function ReporteRubros() {
  const L = useLang();
  const navigate = useNavigate();
  const esCliente = useEsCliente();
  const multimoneda = useMultimoneda();
  const { theme } = useTheme();
  const narrow = useNarrow(880);

  const hoy = ymd(new Date());
  const [aI, aF] = rangoAnio();
  const [modo, setModo] = useState("saldo");
  const [vista, setVista] = useState("CRC");
  const [corte, setCorte] = useState(hoy);
  const [desde, setDesde] = useState(aI);
  const [hasta, setHasta] = useState(aF);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [bajando, setBajando] = useState(null); // "excel" | "pdf" | null
  const [busqueda, setBusqueda] = useState("");
  const [orden, setOrden] = useState({ key: "nombre", dir: "asc" });
  // Rubros: se guardan los OCULTOS (vacío = se ven todos).
  // Filiales: se guardan las ELEGIDAS (vacío = se ven todas).
  const [filtroRubros, setFiltroRubros] = useState(() => new Set());
  const [filtroFiliales, setFiltroFiliales] = useState(() => new Set());
  const [filtroEstado, setFiltroEstado] = useState("");
  const [panelFiliales, setPanelFiliales] = useState(false);
  const [menuEstado, setMenuEstado] = useState(null); // { fila, rect }
  const [guardandoEstado, setGuardandoEstado] = useState(null);

  const esSaldo = modo === "saldo";

  // Al cambiar de vista o de fechas cambian los rubros y las filiales con
  // movimiento: una selección vieja escondería datos nuevos sin avisar.
  useEffect(() => {
    setFiltroRubros(new Set());
    setFiltroFiliales(new Set());
    setFiltroEstado("");
  }, [modo, desde, hasta, corte]);

  const entidad = esCliente ? L("Cliente", "Client") : L("Filial", "Unit");
  const entidades = esCliente ? L("Clientes", "Clients") : L("Filiales", "Units");
  const sym = vista === "USD" ? "$" : "₡";
  const acento = MODO_COLOR[modo];
  const paleta = RUBRO_COLORS[theme] ?? RUBRO_COLORS.dark;
  const gris = GRIS[theme] ?? GRIS.dark;

  const fmt = (v) => {
    const n = Number(v || 0);
    return `${n < 0 ? "-" : ""}${sym}${formatCurrency(Math.abs(n))}`;
  };
  /* Los totales grandes del resumen no necesitan los centavos: ocupan lugar y
     empujan la cifra a un tamaño ilegible en pantallas angostas. */
  const fmtCorto = (v) => {
    const n = Number(v || 0);
    const s = n < 0 ? "-" : "";
    const a = Math.abs(n);
    if (a >= 1_000_000) return `${s}${sym}${(a / 1_000_000).toFixed(2)}M`;
    if (a >= 10_000) return `${s}${sym}${Math.round(a).toLocaleString("en-US")}`;
    return `${s}${sym}${formatCurrency(a)}`;
  };

  const qs = useMemo(() => {
    const p = new URLSearchParams({ modo, moneda: vista });
    if (esSaldo) {
      if (corte) p.set("hasta", corte);
    } else {
      if (desde) p.set("desde", desde);
      if (hasta) p.set("hasta", hasta);
    }
    return p.toString();
  }, [modo, vista, esSaldo, corte, desde, hasta]);

  useEffect(() => {
    let vivo = true;
    setLoading(true);
    setError("");
    apiFetch(`/api/finance/reportes/rubros-filial/?${qs}`)
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then((d) => { if (vivo) setData(d); })
      .catch(() => {
        if (!vivo) return;
        setData(null);
        setError(L("No se pudo cargar el reporte.", "Could not load the report."));
      })
      .finally(() => { if (vivo) setLoading(false); });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qs]);

  const MODOS = [
    { v: "saldo", icon: "fa-scale-unbalanced", lbl: L("Saldo al corte", "Balance at date"),
      ayuda: L("Lo que cada filial debe a la fecha de corte, por rubro. Cuadra con su estado de cuenta.",
               "What each unit owes at the cut-off date, by line item. Matches its account statement.") },
    { v: "facturado", icon: "fa-file-invoice", lbl: L("Facturado", "Billed"),
      ayuda: L("Lo emitido en el período: facturas y notas de débito, menos notas de crédito. No incluye saldos iniciales.",
               "Issued during the period: invoices and debit notes, minus credit notes. Excludes opening balances.") },
    { v: "cobrado", icon: "fa-hand-holding-dollar", lbl: L("Cobrado", "Collected"),
      ayuda: L("Lo que entró en el período, por fecha de pago, sin importar cuándo se emitió la factura.",
               "Received during the period, by payment date, regardless of when the invoice was issued.") },
  ];
  const modoActual = MODOS.find((m) => m.v === modo);

  const rubros = useMemo(() => data?.rubros ?? [], [data]);
  const filas = useMemo(() => data?.filas ?? [], [data]);
  const hayDatos = filas.length > 0;

  /* Orden y color de los rubros: sobre el total SIN filtrar, para que un
     rubro conserve su color aunque se oculten otros. "Saldo a favor" y "Sin
     rubro" van en gris y al final: no son categorías de ingreso. */
  const rubrosBase = useMemo(() => {
    if (!data) return [];
    const reales = rubros
      .filter((r) => r.id !== SIN_RUBRO && r.id !== SALDO_FAVOR)
      .map((r) => ({ ...r, montoFull: data.totales?.[String(r.id)] ?? 0 }))
      .sort((a, b) => b.montoFull - a.montoFull)
      .map((r, i) => ({ ...r, color: i < paleta.length ? paleta[i] : gris }));
    const extra = [];
    if (rubros.some((r) => r.id === SALDO_FAVOR)) {
      extra.push({ id: SALDO_FAVOR, codigo: "", nombre: L("Saldo a favor", "Credit balance"),
        montoFull: data.totales?.["-1"] ?? 0, color: gris });
    }
    if (rubros.some((r) => r.id === SIN_RUBRO)) {
      extra.push({ id: SIN_RUBRO, codigo: "", nombre: L("Sin rubro", "No line item"),
        montoFull: data.totales?.["0"] ?? 0, color: gris, sinRubro: true });
    }
    return [...reales, ...extra];
  }, [data, rubros, paleta, gris, L]);

  const rubrosView = useMemo(
    () => rubrosBase.filter((r) => !filtroRubros.has(r.id)),
    [rubrosBase, filtroRubros]);

  /* Con rubros ocultos los totales del backend dejan de servir: se recalcula
     todo sobre lo visible para que la suma de la pantalla sea la suma de lo
     que la pantalla muestra. El estado de cobro, en cambio, es de la filial:
     depende de su saldo completo, no de las columnas que se estén viendo. */
  const filasCalc = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return filas
      .filter((f) => filtroFiliales.size === 0 || filtroFiliales.has(f.id))
      .filter((f) => !q
        || (f.nombre ?? "").toLowerCase().includes(q)
        || (f.propietario ?? "").toLowerCase().includes(q))
      .map((f) => ({
        ...f,
        estado: estadoEfectivo(f.estado_cobro, f.total),
        totalView: rubrosView.reduce((s, r) => s + (f.valores?.[String(r.id)] ?? 0), 0),
      }))
      .filter((f) => {
        if (!filtroEstado) return true;
        if (filtroEstado === "A_FAVOR") return f.total < -0.004;
        return f.estado === filtroEstado;
      })
      // Una filial cuyos únicos rubros quedaron ocultos sería una fila de
      // guiones sumando cero: no aporta nada.
      .filter((f) => rubrosView.some((r) => f.valores?.[String(r.id)]));
  }, [filas, busqueda, filtroFiliales, filtroEstado, rubrosView]);

  const totalesView = useMemo(() => {
    const m = {};
    rubrosView.forEach((r) => {
      m[r.id] = filasCalc.reduce((s, f) => s + (f.valores?.[String(r.id)] ?? 0), 0);
    });
    return m;
  }, [rubrosView, filasCalc]);
  const totalGeneral = useMemo(
    () => Object.values(totalesView).reduce((s, v) => s + v, 0), [totalesView]);

  /* Composición: participación sobre lo POSITIVO. En modo saldo un rubro
     puede quedar negativo (saldos a favor); ese no ocupa lugar en la barra. */
  const composicion = useMemo(() => {
    const positivo = rubrosView.reduce((s, r) => s + Math.max(0, totalesView[r.id] ?? 0), 0);
    return rubrosView.map((r) => {
      const monto = totalesView[r.id] ?? 0;
      return { ...r, monto, pct: positivo ? (Math.max(0, monto) / positivo) * 100 : 0 };
    });
  }, [rubrosView, totalesView]);

  const maxPorRubro = useMemo(() => {
    const m = {};
    rubrosView.forEach((r) => {
      m[r.id] = filasCalc.reduce((mx, f) => Math.max(mx, f.valores?.[String(r.id)] ?? 0), 0);
    });
    return m;
  }, [rubrosView, filasCalc]);
  const maxTotal = useMemo(
    () => filasCalc.reduce((mx, f) => Math.max(mx, f.totalView ?? 0), 0), [filasCalc]);

  const resumen = useMemo(() => {
    const r = { COBRO_JUDICIAL: [0, 0], ARREGLO: [0, 0], MOROSO: [0, 0], AL_DIA: [0, 0] };
    let favorN = 0, favor = 0;
    filasCalc.forEach((f) => {
      r[f.estado][0] += 1;
      r[f.estado][1] += f.totalView;
      if (f.total < -0.004) { favorN += 1; favor += f.total; }
    });
    return { ...r, favorN, favor };
  }, [filasCalc]);

  const filasVisibles = useMemo(() => {
    const valor = (f) => (orden.key === "total"
      ? (f.totalView ?? 0)
      : (f.valores?.[String(orden.key)] ?? 0));
    const rango = { COBRO_JUDICIAL: 0, ARREGLO: 1, MOROSO: 2, AL_DIA: 3 };
    const copia = [...filasCalc];
    copia.sort((a, b) => {
      let r;
      if (orden.key === "nombre") {
        r = (a.nombre ?? "").localeCompare(b.nombre ?? "", undefined, { numeric: true });
      } else if (orden.key === "estado") {
        r = rango[a.estado] - rango[b.estado];
      } else {
        r = valor(a) - valor(b);
      }
      return orden.dir === "asc" ? r : -r;
    });
    return copia;
  }, [filasCalc, orden]);

  function ordenarPor(key) {
    setOrden((o) => o.key === key
      ? { key, dir: o.dir === "desc" ? "asc" : "desc" }
      : { key, dir: key === "nombre" || key === "estado" ? "asc" : "desc" });
  }
  function toggleRubro(id) {
    setFiltroRubros((s) => {
      const n = new Set(s);
      // Ocultar el último visible dejaría la tabla sin columnas.
      if (!n.has(id) && rubrosBase.length - n.size <= 1) return n;
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }
  function toggleFilial(id) {
    setFiltroFiliales((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }
  function limpiarFiltros() {
    setFiltroRubros(new Set());
    setFiltroFiliales(new Set());
    setFiltroEstado("");
    setBusqueda("");
  }
  const hayFiltros = filtroRubros.size > 0 || filtroFiliales.size > 0 || filtroEstado !== "" || busqueda.trim() !== "";

  /* El archivo baja lo que se está viendo, no el cuadro completo: si no,
     contradiría la pantalla. Las filiales van resueltas (selección + búsqueda
     + estado) para no reimplementar esos filtros en el backend. */
  async function exportar(tipo) {
    setBajando(tipo);
    try {
      const p = new URLSearchParams(qs);
      if (filtroRubros.size > 0) p.set("rubros", rubrosView.map((r) => r.id).join(","));
      if (filtroFiliales.size > 0 || busqueda.trim() || filtroEstado) {
        p.set("filiales", filasCalc.map((f) => f.id).join(",") || "0");
      }
      const fechaArchivo = esSaldo ? corte : hasta || "hoy";
      const url = tipo === "pdf"
        ? `/api/finance/reportes/rubros-filial-pdf/?${p}`
        : `/api/finance/reportes/rubros-filial-excel/?${p}`;
      await downloadExcel(url, `cxc_por_rubro_${modo}_${fechaArchivo}.${tipo === "pdf" ? "pdf" : "xlsx"}`);
    } catch {
      setError(tipo === "pdf"
        ? L("No se pudo generar el PDF.", "Could not generate the PDF.")
        : L("No se pudo generar el Excel.", "Could not generate the Excel file."));
    } finally {
      setBajando(null);
    }
  }

  async function cambiarEstado(fila, valor) {
    setMenuEstado(null);
    setGuardandoEstado(fila.id);
    try {
      const res = await apiFetch(`/api/residents/filial/${fila.id}/`, {
        method: "PATCH",
        body: JSON.stringify({ estado_cobro: valor }),
      });
      if (!res.ok) throw new Error();
      setData((d) => d && {
        ...d,
        filas: d.filas.map((f) => (f.id === fila.id ? { ...f, estado_cobro: valor } : f)),
      });
    } catch {
      setError(L("No se pudo cambiar el estado de cobro.", "Could not change the collection status."));
    } finally {
      setGuardandoEstado(null);
    }
  }

  const presetsCorte = (() => {
    const n = new Date();
    return [
      ["hoy", L("Hoy", "Today"), hoy],
      ["finMes", L("Cierre mes pasado", "End of last month"), ymd(new Date(n.getFullYear(), n.getMonth(), 0))],
      ["finAnio", L(`Cierre ${n.getFullYear() - 1}`, `End of ${n.getFullYear() - 1}`), `${n.getFullYear() - 1}-12-31`],
    ];
  })();
  const periodoActivo = (() => {
    const [m0i, m0f] = rangoMes(0);
    const [m1i, m1f] = rangoMes(-1);
    if (desde === m0i && hasta === m0f) return "mes";
    if (desde === m1i && hasta === m1f) return "mesPasado";
    if (desde === aI && hasta === aF) return "anio";
    if (!desde && !hasta) return "todo";
    return null;
  })();

  const sinRubro = rubrosBase.find((r) => r.sinRubro);
  const montoSinRubro = sinRubro ? (data?.totales?.["0"] ?? 0) : 0;
  const nombreEstado = (k) => (ESTADOS[k] ? L(ESTADOS[k].es, ESTADOS[k].en) : "");
  const rotuloFecha = esSaldo
    ? L(`al ${fechaCorta(corte)}`, `as of ${fechaCorta(corte)}`)
    : (desde || hasta)
      ? `${fechaCorta(desde) || "…"} - ${fechaCorta(hasta) || "…"}`
      : L("todo el histórico", "all time");

  return (
    <>
      <span className={invoiceStyles.inactive}>
        <Link to="/"><span className={invoiceStyles.inactive}>{L("Inicio", "Home")} /</span></Link>
        <Link to="/finanzas/cxc"><span className={invoiceStyles.inactive}> {L("Cuentas por Cobrar", "Receivables")} /</span></Link>
        <span className={invoiceStyles.active}> {L("Por Rubro", "By Line Item")}</span>
      </span>

      {/* ── Encabezado ─────────────────────────────────────────────────── */}
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-3" style={{ margin: "6px 0 16px" }}>
        <div style={{ minWidth: 0 }}>
          <h2 className="mt-2 me-2 p-0 title" style={{ marginBottom: 4 }}>
            {L("Cuentas por Cobrar por Rubro", "Receivables by Line Item")}
          </h2>
          <p className="mb-0" style={{ color: "var(--text-muted)" }}>
            {L(`Cada columna es una cuenta de ingreso; cada fila, una ${entidad.toLowerCase()}`,
               `Each column is an income account; each row, one ${entidad.toLowerCase()}`)}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 8 }}>
          {multimoneda && (
            <CurrencyToggle value={vista} onChange={setVista}
              title={L("Moneda del reporte", "Report currency")} />
          )}
          <BotonExport icon="fa-file-excel" color="#22c55e" ocupado={bajando === "excel"}
            deshabilitado={!!bajando || !hayDatos} onClick={() => exportar("excel")}>
            Excel
          </BotonExport>
          <BotonExport icon="fa-file-pdf" color="#ef4444" ocupado={bajando === "pdf"}
            deshabilitado={!!bajando || !hayDatos} onClick={() => exportar("pdf")}>
            PDF
          </BotonExport>
        </div>
      </div>

      {/* ── Vista + fecha ───────────────────────────────────────────────── */}
      <Card style={{ marginBottom: 14, padding: 0, overflow: "hidden" }}>
        <div role="tablist" style={{
          display: "grid", gridTemplateColumns: narrow ? "minmax(0, 1fr)" : "repeat(3, minmax(0, 1fr))",
          borderBottom: "1px solid var(--border-card)",
        }}>
          {MODOS.map((m, i) => {
            const on = modo === m.v;
            const c = MODO_COLOR[m.v];
            return (
              <button key={m.v} type="button" role="tab" aria-selected={on} onClick={() => setModo(m.v)} title={m.ayuda}
                style={{
                  display: "flex", alignItems: "center", gap: 11, textAlign: "left", minWidth: 0,
                  padding: "13px 16px", border: "none", cursor: "pointer",
                  borderRight: !narrow && i < MODOS.length - 1 ? "1px solid var(--border-card)" : "none",
                  borderBottom: narrow && i < MODOS.length - 1 ? "1px solid var(--border-card)" : "none",
                  boxShadow: on ? `inset 0 -3px 0 ${c}` : "none",
                  background: on ? tinte(c, 0.07) : "transparent",
                  transition: "background 0.15s",
                }}>
                <span style={{
                  width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  background: on ? tinte(c, 0.16) : "var(--hover-bg)",
                  color: on ? c : "var(--text-muted)",
                }}>
                  <i className={`fa-solid ${m.icon}`} style={{ fontSize: "0.85rem" }} />
                </span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: "block", fontWeight: 700, fontSize: "0.88rem",
                    color: on ? "var(--text-primary)" : "var(--text-secondary)" }}>{m.lbl}</span>
                  <span style={{ display: "block", fontSize: "0.72rem", color: "var(--text-muted)",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: narrow ? "normal" : "nowrap" }}>
                    {m.ayuda}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", padding: "11px 14px" }}>
          <i className="fa-regular fa-calendar" style={{ color: "var(--text-muted)", fontSize: "0.8rem", marginRight: 2 }} />
          {esSaldo ? (
            <>
              {presetsCorte.map(([k, lbl, val]) => (
                <PeriodBtn key={k} active={corte === val} color={acento} onClick={() => setCorte(val)}>{lbl}</PeriodBtn>
              ))}
              <FechaInput label={L("Saldo al", "Balance as of")} value={corte} onChange={(v) => setCorte(v || hoy)} />
            </>
          ) : (
            <>
              <PeriodBtn active={periodoActivo === "mes"} color={acento} onClick={() => { const [i, f] = rangoMes(0); setDesde(i); setHasta(f); }}>
                {L("Este mes", "This month")}
              </PeriodBtn>
              <PeriodBtn active={periodoActivo === "mesPasado"} color={acento} onClick={() => { const [i, f] = rangoMes(-1); setDesde(i); setHasta(f); }}>
                {L("Mes pasado", "Last month")}
              </PeriodBtn>
              <PeriodBtn active={periodoActivo === "anio"} color={acento} onClick={() => { setDesde(aI); setHasta(aF); }}>
                {L("Este año", "This year")}
              </PeriodBtn>
              <PeriodBtn active={periodoActivo === "todo"} color={acento} onClick={() => { setDesde(""); setHasta(""); }}>
                {L("Todo", "All time")}
              </PeriodBtn>
              <FechaInput label={L("Desde", "From")} value={desde} onChange={setDesde} />
              <FechaInput label={L("Hasta", "To")} value={hasta} onChange={setHasta} />
            </>
          )}
        </div>
      </Card>

      {error && (
        <div style={{
          background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.35)",
          color: "#ef4444", borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: "0.85rem",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <i className="fa-solid fa-circle-exclamation" />
          <span style={{ flex: 1 }}>{error}</span>
          <button type="button" onClick={() => setError("")} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer" }}>✕</button>
        </div>
      )}

      {/* ── Aviso de saldos sin rubro ───────────────────────────────────── */}
      {!loading && sinRubro && Math.abs(montoSinRubro) > 0.004 && (
        <div style={{
          display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
          background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.3)",
          borderLeft: "4px solid #f59e0b", borderRadius: 10, padding: "11px 14px", marginBottom: 14,
        }}>
          <i className="fa-solid fa-tags" style={{ color: "#f59e0b" }} />
          <span style={{ flex: 1, minWidth: 220, fontSize: "0.84rem", color: "var(--text-secondary)" }}>
            <strong style={{ color: "var(--text-primary)" }}>{fmt(montoSinRubro)}</strong>{" "}
            {L("están en saldos sin rubro asignado (por lo general, saldos iniciales importados). Asignales rubro para verlos en su columna.",
               "are in balances with no line item (usually imported opening balances). Assign them one to see them in their column.")}
          </span>
          <button type="button"
            onClick={() => navigate("/finanzas/recibos", { state: { abrirSaldosSinRubro: true } })}
            style={{
              padding: "6px 13px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: "0.8rem",
              background: "rgba(245,158,11,0.15)", border: "1px solid rgba(245,158,11,0.45)", color: "#f59e0b",
              display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
            }}>
            {L("Asignar rubros", "Assign line items")} <i className="fa-solid fa-arrow-right" style={{ fontSize: "0.7rem" }} />
          </button>
        </div>
      )}

      {loading && !data ? (
        <Card><Vacio texto={L("Cargando…", "Loading…")} icono="fa-spinner fa-spin" /></Card>
      ) : !hayDatos ? (
        <Card>
          <Vacio icono="fa-table-cells"
            texto={esSaldo
              ? L("Ninguna filial tiene saldo a esa fecha.", "No unit has a balance at that date.")
              : L("No hay movimientos en el período seleccionado.", "No activity in the selected period.")} />
        </Card>
      ) : (
        <div style={{ opacity: loading ? 0.55 : 1, transition: "opacity 0.15s" }}>
          {/* ── Resumen ──────────────────────────────────────────────── */}
          <div style={{
            display: "grid", gap: 12, marginBottom: 14,
            gridTemplateColumns: narrow ? "repeat(auto-fit, minmax(150px, 1fr))" : "repeat(4, 1fr)",
          }}>
            {esSaldo ? (
              <>
                <KPI etiqueta={L("Saldo por cobrar", "Balance due")} valor={fmtCorto(totalGeneral)}
                  nota={rotuloFecha} color={acento} icono="fa-scale-unbalanced" destacado />
                <KPI etiqueta={nombreEstado("MOROSO")} valor={fmtCorto(resumen.MOROSO[1])}
                  nota={L(`${resumen.MOROSO[0]} ${entidades.toLowerCase()}`, `${resumen.MOROSO[0]} ${entidades.toLowerCase()}`)}
                  color={ESTADOS.MOROSO.color} icono={ESTADOS.MOROSO.icon}
                  onClick={() => setFiltroEstado(filtroEstado === "MOROSO" ? "" : "MOROSO")} activo={filtroEstado === "MOROSO"} />
                <KPI etiqueta={L("Arreglo y cobro judicial", "Plans & legal")}
                  valor={fmtCorto(resumen.ARREGLO[1] + resumen.COBRO_JUDICIAL[1])}
                  nota={L(`${resumen.ARREGLO[0]} en arreglo · ${resumen.COBRO_JUDICIAL[0]} en cobro judicial`,
                          `${resumen.ARREGLO[0]} on plan · ${resumen.COBRO_JUDICIAL[0]} in legal`)}
                  color={ESTADOS.ARREGLO.color} icono="fa-gavel" />
                <KPI etiqueta={L("Saldos a favor", "Credit balances")} valor={fmtCorto(resumen.favor)}
                  nota={L(`${resumen.favorN} ${entidades.toLowerCase()}`, `${resumen.favorN} ${entidades.toLowerCase()}`)}
                  color="#22c55e" icono="fa-piggy-bank"
                  onClick={() => setFiltroEstado(filtroEstado === "A_FAVOR" ? "" : "A_FAVOR")} activo={filtroEstado === "A_FAVOR"} />
              </>
            ) : (
              <>
                <KPI etiqueta={modoActual.lbl} valor={fmtCorto(totalGeneral)} nota={rotuloFecha}
                  color={acento} icono={modoActual.icon} destacado />
                <KPI etiqueta={L("Rubro principal", "Top line item")}
                  valor={composicion.slice().sort((a, b) => b.monto - a.monto)[0]?.nombre ?? "-"}
                  nota={(() => { const t = composicion.slice().sort((a, b) => b.monto - a.monto)[0]; return t ? `${t.pct.toFixed(1)}% · ${fmtCorto(t.monto)}` : null; })()}
                  color={composicion.slice().sort((a, b) => b.monto - a.monto)[0]?.color ?? gris} icono="fa-trophy" />
                <KPI etiqueta={L(`${entidades} con movimiento`, `${entidades} with activity`)}
                  valor={String(filasCalc.length)} color="#60a5fa" icono="fa-building" />
                <KPI etiqueta={L(`Promedio por ${entidad.toLowerCase()}`, `Average per ${entidad.toLowerCase()}`)}
                  valor={fmtCorto(filasCalc.length ? totalGeneral / filasCalc.length : 0)}
                  color="#a78bfa" icono="fa-chart-simple" />
              </>
            )}
          </div>

          {/* ── Composición por rubro ────────────────────────────────── */}
          <Card style={{ marginBottom: 14 }}>
            <CardTitle icon="fa-layer-group" color={acento}
              title={L("Composición por rubro", "Breakdown by line item")}
              right={<span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{rotuloFecha}</span>} />
            {/* Barra apilada: 2px de separación entre segmentos y tooltip por segmento */}
            <div style={{ display: "flex", gap: 2, height: 14, borderRadius: 7, overflow: "hidden", marginBottom: 16 }}>
              {composicion.filter((r) => r.pct > 0).map((r) => (
                <div key={r.id} title={`${r.nombre}: ${fmt(r.monto)} (${r.pct.toFixed(1)}%)`}
                  style={{ width: `${r.pct}%`, background: r.color, transition: "width 0.25s", minWidth: 2 }} />
              ))}
            </div>
            <div style={{
              display: "grid", gap: "10px 22px",
              gridTemplateColumns: narrow ? "1fr" : "repeat(auto-fill, minmax(250px, 1fr))",
            }}>
              {composicion.map((r) => (
                <div key={r.id} style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: r.color, flexShrink: 0 }} />
                    <span title={r.codigo ? `${r.codigo} - ${r.nombre}` : r.nombre} style={{
                      fontSize: "0.82rem", color: "var(--text-secondary)", flex: 1, minWidth: 0,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      fontStyle: r.sinRubro ? "italic" : "normal",
                    }}>{r.nombre}</span>
                    <span style={{
                      fontSize: "0.82rem", fontWeight: 700, fontVariantNumeric: "tabular-nums",
                      color: r.monto < 0 ? "#ef4444" : "var(--text-primary)",
                    }}>{fmt(r.monto)}</span>
                    <span style={{ fontSize: "0.74rem", color: "var(--text-muted)", minWidth: 44, textAlign: "right",
                      fontVariantNumeric: "tabular-nums" }}>
                      {r.monto > 0 ? `${r.pct.toFixed(1)}%` : "-"}
                    </span>
                  </div>
                  <div style={{ height: 4, borderRadius: 2, background: "var(--hover-bg)", marginTop: 5, marginLeft: 18 }}>
                    <div style={{ width: `${r.pct}%`, height: "100%", borderRadius: 2, background: r.color, opacity: 0.85 }} />
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* ── Detalle ──────────────────────────────────────────────── */}
          <Card style={{ padding: 0, overflow: "hidden" }}>
            {/* Barra de herramientas: búsqueda, estado, rubros, filiales */}
            <div style={{ padding: "12px 14px", borderBottom: "1px solid var(--border-card)", display: "grid", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: "0.74rem", fontWeight: 700, letterSpacing: "0.6px",
                  textTransform: "uppercase", color: "var(--text-muted)" }}>
                  {L("Detalle por ", "Detail by ")}{entidad.toLowerCase()}
                  <span style={{ marginLeft: 8, color: "var(--text-secondary)", textTransform: "none", letterSpacing: 0 }}>
                    {filasVisibles.length !== filas.length ? `${filasVisibles.length} / ${filas.length}` : filas.length}
                  </span>
                </span>
                <div style={{ position: "relative", marginLeft: "auto" }}>
                  <i className="fa-solid fa-magnifying-glass" style={{
                    position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
                    fontSize: "0.72rem", color: "var(--text-muted)", pointerEvents: "none",
                  }} />
                  <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
                    placeholder={L(`Buscar ${entidad.toLowerCase()} o propietario…`, `Search ${entidad.toLowerCase()} or owner…`)}
                    style={{
                      background: "var(--bg)", border: "1px solid var(--border-card)", borderRadius: 8,
                      color: "var(--text-secondary)", padding: "6px 10px 6px 30px", fontSize: "0.8rem",
                      width: narrow ? 190 : 260, outline: "none",
                    }} />
                </div>
              </div>

              {esSaldo && (
                <FiltroFila etiqueta={L("Estado", "Status")}>
                  <Chip on={!filtroEstado} color={acento} onClick={() => setFiltroEstado("")}>{L("Todos", "All")}</Chip>
                  {["MOROSO", "ARREGLO", "COBRO_JUDICIAL", "AL_DIA"].map((k) => (
                    <Chip key={k} on={filtroEstado === k} color={ESTADOS[k].color} icon={ESTADOS[k].icon}
                      onClick={() => setFiltroEstado(filtroEstado === k ? "" : k)}>
                      {nombreEstado(k)} <Cuenta>{resumenCompleto(filas, k)}</Cuenta>
                    </Chip>
                  ))}
                  <Chip on={filtroEstado === "A_FAVOR"} color="#22c55e" icon="fa-piggy-bank"
                    onClick={() => setFiltroEstado(filtroEstado === "A_FAVOR" ? "" : "A_FAVOR")}>
                    {L("A favor", "In credit")} <Cuenta>{filas.filter((f) => f.total < -0.004).length}</Cuenta>
                  </Chip>
                </FiltroFila>
              )}

              <FiltroFila etiqueta={L("Rubros", "Line items")}>
                {rubrosBase.map((r) => {
                  const on = !filtroRubros.has(r.id);
                  return (
                    <button key={r.id} type="button" onClick={() => toggleRubro(r.id)}
                      title={r.codigo ? `${r.codigo} - ${r.nombre}` : r.nombre}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        padding: "4px 11px", borderRadius: 999, cursor: "pointer",
                        fontSize: "0.77rem", fontWeight: 600, maxWidth: 220,
                        border: `1px solid ${on ? tinte(r.color, 0.55) : "var(--border-card)"}`,
                        background: on ? tinte(r.color, 0.13) : "transparent",
                        color: on ? "var(--text-primary)" : "var(--text-muted)",
                        opacity: on ? 1 : 0.6, transition: "all 0.15s",
                        fontStyle: r.sinRubro ? "italic" : "normal",
                      }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, flexShrink: 0, background: on ? r.color : "var(--text-muted)" }} />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.nombre}</span>
                    </button>
                  );
                })}
              </FiltroFila>

              <FiltroFila etiqueta={entidades}>
                <button type="button" onClick={() => setPanelFiliales((v) => !v)}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 7,
                    padding: "4px 12px", borderRadius: 8, cursor: "pointer", fontSize: "0.77rem", fontWeight: 600,
                    border: `1px solid ${filtroFiliales.size ? tinte(acento, 0.5) : "var(--border-card)"}`,
                    background: filtroFiliales.size ? tinte(acento, 0.12) : "transparent",
                    color: filtroFiliales.size ? acento : "var(--text-secondary)",
                  }}>
                  <i className="fa-solid fa-filter" style={{ fontSize: "0.68rem" }} />
                  {filtroFiliales.size
                    ? L(`${filtroFiliales.size} seleccionada(s)`, `${filtroFiliales.size} selected`)
                    : L("Todas", "All")}
                  <i className={`fa-solid fa-chevron-${panelFiliales ? "up" : "down"}`} style={{ fontSize: "0.6rem", opacity: 0.7 }} />
                </button>
                {hayFiltros && (
                  <button type="button" onClick={limpiarFiltros} style={enlaceStyle}>
                    <i className="fa-solid fa-xmark" style={{ marginRight: 5 }} />
                    {L("Limpiar filtros", "Clear filters")}
                  </button>
                )}
              </FiltroFila>

              {panelFiliales && (
                <div style={{
                  maxHeight: 210, overflowY: "auto", display: "grid", gap: 4,
                  gridTemplateColumns: narrow ? "1fr" : "repeat(auto-fill, minmax(220px, 1fr))",
                  paddingTop: 8, borderTop: "1px solid var(--border-card)",
                }}>
                  {filas.map((f) => {
                    const on = filtroFiliales.has(f.id);
                    return (
                      <label key={f.id} style={{
                        display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
                        padding: "5px 8px", borderRadius: 7, fontSize: "0.8rem",
                        background: on ? tinte(acento, 0.1) : "transparent",
                        color: on ? "var(--text-primary)" : "var(--text-secondary)",
                      }}>
                        <input type="checkbox" checked={on} onChange={() => toggleFilial(f.id)}
                          style={{ accentColor: acento, flexShrink: 0 }} />
                        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {f.nombre}
                          {f.propietario && <span style={{ color: "var(--text-muted)" }}> · {f.propietario}</span>}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {filasVisibles.length === 0 ? (
              <Vacio icono="fa-magnifying-glass"
                texto={L("Nada coincide con los filtros.", "Nothing matches the filters.")} />
            ) : narrow ? (
              /* En angosto la matriz no cabe: una tarjeta por filial. */
              <div style={{ padding: 12, display: "grid", gap: 10 }}>
                {filasVisibles.map((f) => (
                  <div key={f.id} style={{
                    border: "1px solid var(--border-card)", borderRadius: 10, padding: "11px 13px",
                    background: "var(--bg)",
                    borderLeft: esSaldo && f.estado !== "AL_DIA" ? `3px solid ${ESTADOS[f.estado].color}` : undefined,
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, color: "var(--text-primary)", fontSize: "0.9rem" }}>{f.nombre}</div>
                        {f.propietario && (
                          <div style={{ fontSize: "0.73rem", color: "var(--text-muted)",
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.propietario}</div>
                        )}
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontWeight: 700, fontSize: "0.95rem", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
                          color: f.totalView < 0 ? "#ef4444" : "var(--text-primary)" }}>{fmt(f.totalView)}</div>
                        {esSaldo && (
                          <EstadoChip estado={f.estado} manual={f.estado_cobro} L={L}
                            cargando={guardandoEstado === f.id}
                            onClick={(e) => setMenuEstado({ fila: f, rect: e.currentTarget.getBoundingClientRect() })} />
                        )}
                      </div>
                    </div>
                    <div style={{ marginTop: 9, display: "grid", gap: 5 }}>
                      {composicion.filter((r) => f.valores?.[String(r.id)]).map((r) => (
                        <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ width: 8, height: 8, borderRadius: 2, background: r.color, flexShrink: 0 }} />
                          <span style={{ fontSize: "0.78rem", color: "var(--text-muted)", flex: 1,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.nombre}</span>
                          <span style={{ fontSize: "0.79rem", fontVariantNumeric: "tabular-nums",
                            color: f.valores[String(r.id)] < 0 ? "#ef4444" : "var(--text-secondary)" }}>
                            {fmt(f.valores[String(r.id)])}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <div style={{
                  display: "flex", justifyContent: "space-between", padding: "11px 13px", borderRadius: 10,
                  background: tinte(acento, 0.1), border: `1px solid ${tinte(acento, 0.3)}`,
                }}>
                  <span style={{ fontWeight: 700, color: "var(--text-primary)", fontSize: "0.85rem" }}>TOTAL</span>
                  <span style={{ fontWeight: 700, color: "var(--text-primary)", fontVariantNumeric: "tabular-nums" }}>
                    {fmt(totalGeneral)}
                  </span>
                </div>
              </div>
            ) : (
              <div style={{ overflow: "auto", maxHeight: "min(70vh, 680px)" }}>
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 560 }}>
                  <thead>
                    <tr>
                      <Th first onClick={() => ordenarPor("nombre")} orden={orden} col="nombre">{entidad}</Th>
                      {composicion.map((r) => (
                        <Th key={r.id} align="right" title={r.codigo ? `${r.codigo} - ${r.nombre}` : r.nombre}
                          onClick={() => ordenarPor(r.id)} orden={orden} col={r.id} dot={r.color}>
                          {r.nombre}
                        </Th>
                      ))}
                      <Th align="right" strong onClick={() => ordenarPor("total")} orden={orden} col="total">
                        {esSaldo ? L("Total global", "Grand total") : L("Total", "Total")}
                      </Th>
                      {esSaldo && (
                        <Th align="center" onClick={() => ordenarPor("estado")} orden={orden} col="estado">
                          {L("Estado", "Status")}
                        </Th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filasVisibles.map((f) => {
                      const acentoFila = esSaldo && f.estado !== "AL_DIA" ? ESTADOS[f.estado].color : null;
                      const fondoFila = esSaldo && (f.estado === "ARREGLO" || f.estado === "COBRO_JUDICIAL")
                        ? tinte(ESTADOS[f.estado].color, 0.07) : undefined;
                      return (
                        <tr key={f.id} className="rubro-row">
                          <Td first bg={fondoFila} acento={acentoFila}>
                            <div style={{ fontWeight: 600, color: "var(--text-primary)" }}>{f.nombre}</div>
                            {f.propietario && (
                              <div style={{ fontSize: "0.73rem", color: "var(--text-muted)", maxWidth: 240,
                                overflow: "hidden", textOverflow: "ellipsis" }} title={f.propietario}>{f.propietario}</div>
                            )}
                          </Td>
                          {composicion.map((r) => {
                            const v = f.valores?.[String(r.id)];
                            const mx = maxPorRubro[r.id] || 0;
                            return (
                              <Td key={r.id} align="right" muted={!v} negativo={v < 0}
                                bg={v > 0 && mx ? tinte(r.color, 0.04 + (v / mx) * 0.17) : fondoFila}>
                                {v ? fmt(v) : "-"}
                              </Td>
                            );
                          })}
                          <Td align="right" strong negativo={f.totalView < 0}
                            bg={f.totalView > 0 && maxTotal ? tinte(acento, 0.04 + (f.totalView / maxTotal) * 0.14) : fondoFila}>
                            {fmt(f.totalView)}
                          </Td>
                          {esSaldo && (
                            <Td align="center" bg={fondoFila}>
                              <EstadoChip estado={f.estado} manual={f.estado_cobro} L={L}
                                cargando={guardandoEstado === f.id}
                                onClick={(e) => setMenuEstado({ fila: f, rect: e.currentTarget.getBoundingClientRect() })} />
                            </Td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <Td first footer strong>TOTAL</Td>
                      {composicion.map((r) => (
                        <Td key={r.id} align="right" footer strong negativo={r.monto < 0}>{fmt(r.monto)}</Td>
                      ))}
                      <Td align="right" footer strong negativo={totalGeneral < 0}>{fmt(totalGeneral)}</Td>
                      {esSaldo && <Td footer />}
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Card>

          {esSaldo && (
            <p style={{ marginTop: 10, fontSize: "0.76rem", color: "var(--text-muted)" }}>
              <i className="fa-solid fa-circle-info" style={{ marginRight: 6, opacity: 0.7 }} />
              {L("El estado se calcula solo (Moroso si tiene saldo, Al día si no) salvo que lo marques a mano: tocá el estado de una fila para cambiarlo. Arreglo de pago y Cobro judicial solo existen marcándolos.",
                 "Status is automatic (Overdue with a balance, Up to date otherwise) unless you set it: click a row's status to change it. Payment plan and Legal collection only exist when set manually.")}
            </p>
          )}
        </div>
      )}

      {menuEstado && (
        <MenuEstado
          fila={menuEstado.fila} rect={menuEstado.rect} L={L}
          onElegir={(v) => cambiarEstado(menuEstado.fila, v)}
          onCerrar={() => setMenuEstado(null)}
        />
      )}

      {/* El realce del hover va como background-image: se compone ENCIMA del
          tinte de magnitud sin pisarlo y se ve igual en claro y oscuro. */}
      <style>{`.rubro-row:hover td {
        background-image: linear-gradient(rgba(148,163,184,0.12), rgba(148,163,184,0.12));
      }`}</style>
    </>
  );
}

/* Cuántas filas tiene cada estado en el cuadro completo (para los chips). */
function resumenCompleto(filas, estado) {
  return filas.filter((f) => estadoEfectivo(f.estado_cobro, f.total) === estado).length;
}

/* ─── Piezas de UI ─────────────────────────────────────────────────────── */

const enlaceStyle = {
  background: "none", border: "none", padding: "4px 2px", cursor: "pointer",
  fontSize: "0.76rem", fontWeight: 600, color: "var(--text-muted)",
  textDecoration: "underline", textUnderlineOffset: 3,
};

function Card({ children, style }) {
  return (
    <div style={{
      background: "var(--bg-card)", border: "1px solid var(--border-card)",
      borderRadius: 14, padding: "16px 18px",
      boxShadow: "0 1px 2px rgba(0,0,0,0.04)", ...style,
    }}>{children}</div>
  );
}

function CardTitle({ icon, color, title, right }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
      <span style={{
        width: 28, height: 28, borderRadius: 8, flexShrink: 0,
        background: `${color}16`, border: `1px solid ${color}2e`,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
      }}>
        <i className={`fa-solid ${icon}`} style={{ color, fontSize: "0.76rem" }} />
      </span>
      <span style={{
        fontSize: "0.74rem", fontWeight: 700, letterSpacing: "0.6px",
        textTransform: "uppercase", color: "var(--text-muted)", flex: 1,
      }}>{title}</span>
      {right}
    </div>
  );
}

function KPI({ etiqueta, valor, nota, color, icono, destacado, onClick, activo }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag type={onClick ? "button" : undefined} onClick={onClick} style={{
      textAlign: "left", font: "inherit",
      background: destacado || activo ? tinte(color, 0.08) : "var(--bg-card)",
      border: `1px solid ${destacado || activo ? tinte(color, 0.35) : "var(--border-card)"}`,
      borderRadius: 14, padding: "13px 15px", minWidth: 0,
      cursor: onClick ? "pointer" : "default", transition: "all 0.15s",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 7 }}>
        <i className={`fa-solid ${icono}`} style={{ color, fontSize: "0.72rem" }} />
        <span style={{
          fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.5px",
          textTransform: "uppercase", color: "var(--text-muted)",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{etiqueta}</span>
        {onClick && (
          <i className="fa-solid fa-filter" title="Filtrar" style={{ marginLeft: "auto", fontSize: "0.6rem",
            color: activo ? color : "var(--text-muted)", opacity: activo ? 1 : 0.5 }} />
        )}
      </div>
      <div style={{
        fontSize: destacado ? "1.5rem" : "1.2rem", fontWeight: 700,
        color: "var(--text-primary)", fontVariantNumeric: "tabular-nums", lineHeight: 1.15,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>{valor}</div>
      {nota && (
        <div style={{ fontSize: "0.73rem", color: "var(--text-muted)", marginTop: 4,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{nota}</div>
      )}
    </Tag>
  );
}

function BotonExport({ icon, color, ocupado, deshabilitado, onClick, children }) {
  return (
    <button type="button" onClick={onClick} disabled={deshabilitado}
      style={{
        display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 14px",
        borderRadius: 8, border: `1px solid ${tinte(color, 0.4)}`,
        background: tinte(color, 0.14), color,
        fontWeight: 600, fontSize: "0.83rem",
        cursor: deshabilitado ? "not-allowed" : "pointer",
        opacity: deshabilitado && !ocupado ? 0.55 : 1,
      }}>
      <i className={`fa-solid ${ocupado ? "fa-spinner fa-spin" : icon}`} />
      {children}
    </button>
  );
}

function PeriodBtn({ active, onClick, color = "#f97316", children }) {
  return (
    <button type="button" onClick={onClick} style={{
      padding: "5px 12px", borderRadius: 7, fontSize: "0.78rem", fontWeight: 600, cursor: "pointer",
      border: `1px solid ${active ? color : "var(--border-card)"}`,
      background: active ? tinte(color, 0.12) : "transparent",
      color: active ? color : "var(--text-secondary)", transition: "all 0.15s",
    }}>{children}</button>
  );
}

function FechaInput({ label, value, onChange }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.78rem", color: "var(--text-muted)" }}>
      {label}
      <input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)}
        style={{
          background: "var(--bg)", border: "1px solid var(--border-card)", borderRadius: 7,
          color: "var(--text-secondary)", padding: "5px 9px", fontSize: "0.8rem", colorScheme: "light dark",
        }} />
    </label>
  );
}

function FiltroFila({ etiqueta, children }) {
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
      <span style={{
        fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase",
        color: "var(--text-muted)", minWidth: 62,
      }}>{etiqueta}</span>
      {children}
    </div>
  );
}

function Chip({ on, color, icon, onClick, children }) {
  return (
    <button type="button" onClick={onClick} style={{
      display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 11px",
      borderRadius: 999, cursor: "pointer", fontSize: "0.77rem", fontWeight: 600,
      border: `1px solid ${on ? tinte(color, 0.55) : "var(--border-card)"}`,
      background: on ? tinte(color, 0.13) : "transparent",
      color: on ? "var(--text-primary)" : "var(--text-secondary)", transition: "all 0.15s",
    }}>
      {icon && <i className={`fa-solid ${icon}`} style={{ fontSize: "0.68rem", color }} />}
      {children}
    </button>
  );
}

function Cuenta({ children }) {
  return (
    <span style={{ fontSize: "0.7rem", color: "var(--text-muted)", fontVariantNumeric: "tabular-nums" }}>{children}</span>
  );
}

function EstadoChip({ estado, manual, onClick, cargando, L }) {
  const e = ESTADOS[estado] ?? ESTADOS.AL_DIA;
  const auto = !ESTADOS[manual];
  return (
    <button type="button" onClick={onClick} disabled={cargando}
      title={auto
        ? L("Automático según el saldo. Tocá para marcarlo a mano.", "Automatic from the balance. Click to set it manually.")
        : L("Marcado a mano. Tocá para cambiarlo.", "Set manually. Click to change it.")}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px",
        borderRadius: 999, cursor: "pointer", fontSize: "0.72rem", fontWeight: 700, whiteSpace: "nowrap",
        border: `1px ${auto ? "dashed" : "solid"} ${tinte(e.color, 0.5)}`,
        background: tinte(e.color, 0.12), color: e.color,
      }}>
      <i className={`fa-solid ${cargando ? "fa-circle-notch fa-spin" : e.icon}`} style={{ fontSize: "0.64rem" }} />
      {L(e.corto[0], e.corto[1])}
      <i className="fa-solid fa-chevron-down" style={{ fontSize: "0.5rem", opacity: 0.7 }} />
    </button>
  );
}

function MenuEstado({ fila, rect, onElegir, onCerrar, L }) {
  const ref = useRef(null);
  useEffect(() => {
    function fuera(e) { if (ref.current && !ref.current.contains(e.target)) onCerrar(); }
    function tecla(e) { if (e.key === "Escape") onCerrar(); }
    document.addEventListener("mousedown", fuera);
    document.addEventListener("keydown", tecla);
    window.addEventListener("scroll", onCerrar, true);
    window.addEventListener("resize", onCerrar);
    return () => {
      document.removeEventListener("mousedown", fuera);
      document.removeEventListener("keydown", tecla);
      window.removeEventListener("scroll", onCerrar, true);
      window.removeEventListener("resize", onCerrar);
    };
  }, [onCerrar]);

  const ancho = 230;
  const left = Math.min(Math.max(8, rect.right - ancho), window.innerWidth - ancho - 8);
  const abajo = rect.bottom + 260 < window.innerHeight;
  const opciones = [
    ["", "fa-wand-magic-sparkles", L("Automático", "Automatic"), L("Moroso si tiene saldo, Al día si no", "Overdue with a balance, else up to date"), "#64748b"],
    ...["MOROSO", "ARREGLO", "COBRO_JUDICIAL", "AL_DIA"].map((k) => [k, ESTADOS[k].icon, L(ESTADOS[k].es, ESTADOS[k].en), null, ESTADOS[k].color]),
  ];
  const actual = fila.estado_cobro || "";

  return createPortal(
    <div ref={ref} role="menu" style={{
      position: "fixed", left, width: ancho, zIndex: 10050,
      ...(abajo ? { top: rect.bottom + 6 } : { bottom: window.innerHeight - rect.top + 6 }),
      background: "var(--bg-card)", border: "1px solid var(--border-card)", borderRadius: 10,
      boxShadow: "0 16px 40px rgba(0,0,0,0.35)", padding: 6,
    }}>
      <div style={{ padding: "6px 9px 8px", fontSize: "0.7rem", color: "var(--text-muted)", fontWeight: 700,
        textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {L("Estado de cobro", "Collection status")} · {fila.nombre}
      </div>
      {opciones.map(([v, icon, lbl, sub, color]) => (
        <button key={v || "auto"} type="button" role="menuitemradio" aria-checked={actual === v}
          onClick={() => onElegir(v)}
          style={{
            width: "100%", display: "flex", alignItems: "center", gap: 9, textAlign: "left",
            padding: "7px 9px", border: "none", borderRadius: 7, cursor: "pointer",
            background: actual === v ? "var(--hover-bg)" : "transparent", color: "var(--text-primary)",
          }}
          onMouseOver={(e) => (e.currentTarget.style.background = "var(--hover-bg)")}
          onMouseOut={(e) => (e.currentTarget.style.background = actual === v ? "var(--hover-bg)" : "transparent")}>
          <i className={`fa-solid ${icon}`} style={{ color, width: 14, fontSize: "0.75rem" }} />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: "block", fontSize: "0.8rem", fontWeight: 600 }}>{lbl}</span>
            {sub && <span style={{ display: "block", fontSize: "0.68rem", color: "var(--text-muted)" }}>{sub}</span>}
          </span>
          {actual === v && <i className="fa-solid fa-check" style={{ color: "#22c55e", fontSize: "0.7rem" }} />}
        </button>
      ))}
    </div>,
    document.body,
  );
}

/* Primera columna fija al desplazar en horizontal y cabecera fija en
   vertical: con muchos rubros y filiales, sin esto uno pierde de vista de
   quién y de qué es la cifra que está mirando. */
function Th({ children, align = "left", first, strong, title, dot, onClick, orden, col }) {
  const activo = orden && orden.key === col;
  return (
    <th title={title} onClick={onClick} style={{
      textAlign: align, padding: "11px 14px", fontSize: "0.71rem", fontWeight: 700,
      letterSpacing: "0.4px", textTransform: "uppercase",
      color: activo || strong ? "var(--text-primary)" : "var(--text-muted)",
      borderBottom: "1px solid var(--border-card)", whiteSpace: "nowrap",
      background: "var(--bg-card)", cursor: onClick ? "pointer" : "default", userSelect: "none",
      position: "sticky", top: 0, zIndex: first ? 3 : 2,
      ...(first ? { left: 0, boxShadow: "1px 0 0 var(--border-card)" } : null),
    }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        {dot && <span style={{ width: 8, height: 8, borderRadius: 2, background: dot, flexShrink: 0 }} />}
        {children}
        {activo && (
          <i className={`fa-solid fa-caret-${orden.dir === "asc" ? "up" : "down"}`} style={{ fontSize: "0.7rem", opacity: 0.8 }} />
        )}
      </span>
    </th>
  );
}

function Td({ children, align = "left", first, strong, muted, negativo, bg, footer, acento }) {
  return (
    <td style={{
      textAlign: align, padding: "9px 14px", fontSize: "0.84rem", whiteSpace: "nowrap",
      fontWeight: strong ? 700 : 400, fontVariantNumeric: "tabular-nums",
      color: negativo ? "#ef4444" : muted ? "var(--text-muted)" : strong ? "var(--text-primary)" : "var(--text-secondary)",
      borderBottom: "1px solid var(--border-card)",
      background: footer ? "var(--bg-card)" : bg,
      ...(footer ? { position: "sticky", bottom: 0, zIndex: first ? 3 : 1, borderTop: "1px solid var(--border-card)" } : null),
      ...(first ? {
        position: "sticky", left: 0, zIndex: footer ? 3 : 1,
        background: footer ? "var(--bg-card)" : (bg ? `linear-gradient(${bg}, ${bg}), var(--bg-card)` : "var(--bg-card)"),
        boxShadow: `${acento ? `inset 3px 0 0 ${acento}, ` : ""}1px 0 0 var(--border-card)`,
      } : null),
    }}>{children}</td>
  );
}

function Vacio({ texto, icono }) {
  return (
    <div style={{ padding: "48px 20px", textAlign: "center", color: "var(--text-muted)" }}>
      <i className={`fa-solid ${icono}`} style={{ fontSize: "1.7rem", opacity: 0.5, display: "block", marginBottom: 10 }} />
      <span style={{ fontSize: "0.88rem" }}>{texto}</span>
    </div>
  );
}
